import { Card, CardHeader, Dropdown, Field, Input, Option, Title3, Checkbox, CheckboxOnChangeData, CardPreview, Divider } from "@fluentui/react-components";
import { ChangeEvent, Dispatch, FC, SetStateAction, useState, useEffect } from "react";
import { Tendering, TenderingPart } from "../../../models/tendering";
import { Organization } from "../../../models/organizations";
import { getOrganizations } from "../../../services/organizationService";

interface TenderingPartsProps {
  tenderingDocument: Tendering;
  updateTenderingData: Dispatch<SetStateAction<Tendering>>;
}

const TenderingTenderingPart: FC<TenderingPartsProps> = ({
  tenderingDocument,
  updateTenderingData
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await getOrganizations();
        setOrganizations(response.filter((org: Organization) => org.organizationTypeID == 2)); 
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    if(tenderingDocument.tenderingParts.length > 0){
      fetchOrganizations();
    }
  }, [])

  const selectedTenderingPart = selectedIndex >= 0 ? tenderingDocument.tenderingParts[selectedIndex] : undefined;

  const handleDropdownChange = (_: any, data: any) => {
    const index = tenderingDocument.tenderingParts.findIndex(tp => tp.tenderingPart === data.optionText);
    setSelectedIndex(index);
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    data: { value: string }
  ) => {
    if (selectedIndex < 0) return;

    const name = event.target.name;
    const value = name === "listPrio" ? Number(data.value) : data.value;

    updateTenderingData((prev) => {
      const updatedArray = [...prev.tenderingParts];
      updatedArray[selectedIndex] = {
        ...updatedArray[selectedIndex],
        [name]: value
      };

      return {
        ...prev,
        tenderingParts: updatedArray
      };
    });
  };

  const handleChangeBoolean = (
    _: any,
    data: CheckboxOnChangeData
  ) => {
    if (selectedIndex < 0) return;

    const value: boolean = Boolean(data.checked);

    updateTenderingData((prev) => {
      const updatedArray = [...prev.tenderingParts];
      updatedArray[selectedIndex] = {
        ...updatedArray[selectedIndex],
        evaluated: value,
        // Manual activation supersedes any scheduled activation
        ...(value ? { activateOnContractStart: undefined } : {})
      };

      return {
        ...prev,
        tenderingParts: updatedArray
      };
    });
  };

  const handleActivateOnContractStartChange = (
    _: any,
    data: CheckboxOnChangeData
  ) => {
    if (selectedIndex < 0) return;

    const value = data.checked ? true : undefined;

    updateTenderingData((prev) => {
      const updatedArray = [...prev.tenderingParts];
      updatedArray[selectedIndex] = {
        ...updatedArray[selectedIndex],
        activateOnContractStart: value
      };

      return {
        ...prev,
        tenderingParts: updatedArray
      };
    });
  };

  const handleOrganizationChange = (_: any, data: any) => {
    if (selectedIndex < 0) return;

    const selectedOrgIds = data.selectedOptions.map((opt: string) => Number(opt));

    updateTenderingData((prev) => {
      const updatedArray = [...prev.tenderingParts];
      updatedArray[selectedIndex] = {
        ...updatedArray[selectedIndex],
        allowedOrganizations: selectedOrgIds
      };

      return {
        ...prev,
        tenderingParts: updatedArray
      };
    });
  };

  return(
    <Card className="flex-1">
        <CardPreview>
          <Title3>Delområden</Title3>
        </CardPreview>
        <Field label="Välj delområde att redigera">
          <Dropdown
            value={selectedTenderingPart?.tenderingPart || ""}
            onOptionSelect={handleDropdownChange}
          >
            {tenderingDocument.tenderingParts.map((option, index) => (
              <Option key={index} value={option.tenderingPart}>
                {option.tenderingPart}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Divider />
        <Field
          label="Namn på avtalsområdet:"
        >
          <Input 
            name="tenderingPart" 
            value={selectedTenderingPart?.tenderingPart || ""} 
            onChange={handleChange}
            disabled={!selectedTenderingPart}
          />
        </Field>
        <Field label="Ska avtalsområde vara sökbar?">
          <Checkbox
            checked={selectedTenderingPart?.evaluated || false}
            onChange={handleChangeBoolean}
            label="Ja, den ska vara sökbar" 
            disabled={!selectedTenderingPart}
          />
        </Field>
        <Field
          label="Avtalsstart:"
          hint="Området blir sökbart automatiskt på det planerade aktiveringsdatum som anges under Information."
        >
          <Checkbox
            checked={selectedTenderingPart?.activateOnContractStart || false}
            onChange={handleActivateOnContractStartChange}
            label="Aktivera vid avtalsstart"
            disabled={!selectedTenderingPart || selectedTenderingPart.evaluated}
          />
        </Field>
        <Field label="List prioritet">
          <Input
            name="listPrio"
            value={selectedTenderingPart?.listPrio?.toString() || ""}
            onChange={handleChange} 
            min={1} max={5} type="number"
            disabled={!selectedTenderingPart}
            />
        </Field>
        <Field label="Begränsa till urvalda organisationer:">
          <Dropdown 
            multiselect={true} 
            disabled={!selectedTenderingPart}
            onOptionSelect={handleOrganizationChange}
            selectedOptions={selectedTenderingPart?.allowedOrganizations?.map(id => String(id)) || []}
            placeholder="Välj organisationer"
            positioning={{autoSize: false}}
              listbox={{
                style: {
                  maxHeight: '300px',
                  overflowY: 'auto'
                },
              }}
          >
            {organizations.map((org) => (
              <Option key={org.organizationID} value={String(org.organizationID)}>
                {org.organizationName}
              </Option>
            ))}
          </Dropdown>
        </Field>
    </Card>
  )
}

export default TenderingTenderingPart;